import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { trpc } from '../api/trpc';
import { ConnectorForm } from './ConnectorForm';
import {
  getCatalogEntry,
  getAvailableTypes,
  getUpcomingTypes,
  getCatalogIcon,
} from '../connectors/catalog';

// Per-connector live connection-test state. The status badge is driven
// entirely by this — the page tests every connector on open, so the user
// never sees a stale status.
type TestState =
  | { status: 'testing' }
  | { status: 'ok'; message: string }
  | { status: 'failed'; message: string };

// Badge class + label for a connector's current test state. "No state yet"
// (test about to start) is treated as "testing" so the badge never flashes a
// stale "Connected".
function badgeMeta(state: TestState | undefined): { cls: string; label: string } {
  if (!state || state.status === 'testing') return { cls: 'status-testing', label: 'Testing…' };
  if (state.status === 'ok') return { cls: 'status-connected', label: 'Connected' };
  return { cls: 'status-error', label: 'Connection failed' };
}

// Render an icon by using the SVG as a mask, painted with currentColor.
// `<img src>` would lose theme adaptability; mask-image keeps the icon a
// single color driven by `background-color` (set in the CSS class).
function iconMaskStyle(url: string): CSSProperties {
  return {
    WebkitMaskImage: `url("${url}")`,
    maskImage: `url("${url}")`,
  };
}

interface Connector {
  id: string;
  productType: string;
  name: string;
  config: unknown;
}

interface InterestRow {
  id: string;
  productType: string;
}

interface InterestData {
  mine: InterestRow[];
  teamCounts: Record<string, number>;
}

export function ConnectorList() {
  const utils = trpc.useUtils();
  const { data: connectors, refetch } = trpc.connectors.list.useQuery();
  const { data: interestData } = trpc.connectors.listInterest.useQuery();

  const testMut = trpc.connectors.testConnection.useMutation();

  const registerMut = trpc.connectors.registerInterest.useMutation({
    onMutate: async ({ productType }) => {
      await utils.connectors.listInterest.cancel();
      const prev = utils.connectors.listInterest.getData() as InterestData | undefined;
      utils.connectors.listInterest.setData(undefined, ((old: InterestData | undefined) => ({
        mine: [
          ...(old?.mine ?? []),
          { id: `optimistic-${productType}`, productType },
        ],
        teamCounts: {
          ...(old?.teamCounts ?? {}),
          [productType]: (old?.teamCounts?.[productType] ?? 0) + 1,
        },
      })) as never);
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) utils.connectors.listInterest.setData(undefined, ctx.prev as never);
    },
    onSettled: () => { void utils.connectors.listInterest.invalidate(); },
  });

  const cancelInterestMut = trpc.connectors.cancelInterest.useMutation({
    onMutate: async ({ productType }) => {
      await utils.connectors.listInterest.cancel();
      const prev = utils.connectors.listInterest.getData() as InterestData | undefined;
      utils.connectors.listInterest.setData(undefined, ((old: InterestData | undefined) => ({
        mine: (old?.mine ?? []).filter((m: InterestRow) => m.productType !== productType),
        teamCounts: {
          ...(old?.teamCounts ?? {}),
          [productType]: Math.max(0, (old?.teamCounts?.[productType] ?? 1) - 1),
        },
      })) as never);
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) utils.connectors.listInterest.setData(undefined, ctx.prev as never);
    },
    onSettled: () => { void utils.connectors.listInterest.invalidate(); },
  });

  const [showForm, setShowForm] = useState(false);
  const [editingConnector, setEditingConnector] = useState<Connector | null>(null);
  const [preselectedType, setPreselectedType] = useState('');
  const [testStates, setTestStates] = useState<Record<string, TestState>>({});
  // IDs already auto-tested this mount — guards the effect against retesting
  // on every list-query refetch.
  const testedIdsRef = useRef<Set<string>>(new Set());

  const connectorList: Connector[] = (connectors as Connector[] | undefined) ?? [];

  // Resolve legacy aliases (e.g. 'sentinel' → 'microsoft') so a Sentinel
  // connector hides the Microsoft "Available" card.
  const connectedCanonicalTypes = new Set<string>();
  connectorList.forEach((c) => {
    const canonical = getCatalogEntry(c.productType)?.type ?? c.productType;
    connectedCanonicalTypes.add(canonical);
  });

  const interest = interestData as InterestData | undefined;
  const myInterestSet = new Set((interest?.mine ?? []).map((i: InterestRow) => i.productType));
  const teamCounts = interest?.teamCounts ?? {};

  // Run the real connection test for one connector and record the outcome.
  function runTest(id: string) {
    setTestStates((prev) => ({ ...prev, [id]: { status: 'testing' } }));
    testMut
      .mutateAsync({ connectorId: id })
      .then((result) => {
        const r = result as { ok: boolean; message: string };
        setTestStates((prev) => ({
          ...prev,
          [id]: r.ok
            ? { status: 'ok', message: r.message }
            : { status: 'failed', message: r.message },
        }));
      })
      .catch(() => {
        setTestStates((prev) => ({ ...prev, [id]: { status: 'failed', message: 'Test failed' } }));
      });
  }

  // Auto-test every connector when the page opens. The ref guard tests each
  // connector exactly once per mount, even as the list query refetches; a
  // connector added mid-session is tested when it first appears.
  useEffect(() => {
    if (!connectors) return;
    for (const c of connectorList) {
      if (testedIdsRef.current.has(c.id)) continue;
      testedIdsRef.current.add(c.id);
      runTest(c.id);
    }
  }, [connectors]);

  // Auto-open Microsoft wizard when returning from provisioning callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('msProvisioning') || params.get('provisioningId')) {
      setPreselectedType('microsoft');
      setEditingConnector(null);
      setShowForm(true);
    }
  }, []);

  const availableNotConnected = getAvailableTypes().filter(
    (e) => !connectedCanonicalTypes.has(e.type),
  );
  const upcoming = getUpcomingTypes();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 className="settings-section-title">Connectors</h2>
          <p className="settings-section-desc">Connect your security tools to enable Cade to query and correlate data.</p>
        </div>
        <button className="btn-recommended" onClick={() => { setEditingConnector(null); setPreselectedType(''); setShowForm(true); }}>
          Add connector
        </button>
      </div>

      {connectorList.length > 0 && (
        <section className="connector-section">
          <h3 className="connector-section-title">Connected</h3>
          <div className="connector-grid">
            {connectorList.map((c) => {
              const entry = getCatalogEntry(c.productType);
              const label = entry?.label ?? c.productType;
              const isLegacyAlias = entry && entry.type !== c.productType;
              const test = testStates[c.id];
              const badge = badgeMeta(test);
              const testDone = test !== undefined && test.status !== 'testing';
              return (
                <div key={c.id} className="connector-card">
                  <div className="connector-card-title-row">
                    <span
                      className="connector-card-icon"
                      style={iconMaskStyle(getCatalogIcon(c.productType))}
                      aria-hidden="true"
                    />
                    <div className="connector-card-title-text">
                      <div className="connector-card-name">
                        {label}
                        {isLegacyAlias && <span className="connector-card-legacy"> (legacy)</span>}
                      </div>
                    </div>
                    {testDone ? (
                      <button
                        type="button"
                        className={`status-badge ${badge.cls} connector-badge-btn`}
                        onClick={() => runTest(c.id)}
                        title={`Re-test ${label} connection`}
                        aria-label={`Re-test ${label} connection`}
                      >
                        <span className="connector-badge-content">
                          <span className="status-badge-dot" />
                          {badge.label}
                        </span>
                        <span className="connector-badge-refresh" aria-hidden="true">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                            <path d="M3 3v5h5" />
                            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                            <path d="M21 21v-5h-5" />
                          </svg>
                        </span>
                      </button>
                    ) : (
                      <span className={`status-badge ${badge.cls}`}>
                        <span className="status-badge-dot" />
                        {badge.label}
                      </span>
                    )}
                  </div>

                  <div className="connector-card-actions">
                    <button className="btn-secondary" onClick={() => { setEditingConnector(c); setShowForm(true); }}>
                      Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="connector-section">
        <h3 className="connector-section-title">Available to connect</h3>
        <div className="connector-grid">
          {availableNotConnected.map((e) => (
            <div key={e.type} className="connector-card">
              <div className="connector-card-title-row">
                <span
                  className="connector-card-icon"
                  style={iconMaskStyle(e.iconPath)}
                  aria-hidden="true"
                />
                <div className="connector-card-title-text">
                  <div className="connector-card-name">{e.label}</div>
                  <div className="connector-card-description">{e.shortDescription}</div>
                </div>
              </div>
              <div className="connector-card-actions">
                <button className="btn-primary" onClick={() => { setEditingConnector(null); setPreselectedType(e.type); setShowForm(true); }}>
                  Connect
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="connector-section">
        <h3 className="connector-section-title">Coming soon</h3>
        <div className="connector-grid">
          {upcoming.map((e) => {
            const isRegistered = myInterestSet.has(e.type);
            const totalCount = teamCounts[e.type] ?? 0;
            // Subtract the current user's own row from the "others" count so
            // we never say "0 others" for someone who just registered.
            const othersCount = isRegistered ? Math.max(0, totalCount - 1) : totalCount;
            return (
              <div key={e.type} className="connector-card connector-card-upcoming">
                <div className="connector-card-title-row">
                  <span
                    className="connector-card-icon connector-card-icon-muted"
                    style={iconMaskStyle(e.iconPath)}
                    aria-hidden="true"
                  />
                  <div className="connector-card-title-text">
                    <div className="connector-card-name">{e.label}</div>
                    <div className="connector-card-description">{e.shortDescription}</div>
                  </div>
                </div>
                <div className="connector-notify-row">
                  {isRegistered ? (
                    <>
                      <button className="btn-secondary" disabled>✓ You'll be notified</button>
                      <button
                        type="button"
                        className="connector-notify-cancel"
                        onClick={() => cancelInterestMut.mutate({ productType: e.type })}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-secondary"
                      onClick={() => registerMut.mutate({ productType: e.type })}
                      disabled={registerMut.isPending}
                    >
                      Notify me when available
                    </button>
                  )}
                </div>
                {othersCount > 0 && (
                  <div className="connector-team-interest">
                    {othersCount} {othersCount === 1 ? 'other' : 'others'} on your team also want this
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {showForm && (
        <ConnectorForm
          connector={editingConnector}
          preselectedType={preselectedType}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); void refetch(); }}
        />
      )}
    </div>
  );
}
